import React, { useState, useRef, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { Dropdown } from "react-bootstrap";
import { FiChevronLeft, FiChevronRight, FiChevronDown, FiChevronUp, FiEdit2, FiCheck, FiX, FiEye, FiEyeOff } from "react-icons/fi";
import { buildCentroids, haversineMetres, matchBle, GPS_THRESHOLD_M, resolveLocationLabel } from './asset-tracking-utils';
import { selectAirportLocations } from '_redux/slices/timeline/timeline-areas';
import { selectWifiLocations } from '_redux/slices/asset-tracking/asset-tracking';

const BRAND      = '#307FE2';
const BRAND_DARK = '#1e5fbb';

// Format helpers
function fmtDate(val) {
  if (!val) return '-';
  try {
    const d = val?.toDate ? val.toDate() : (typeof val === 'string' ? new Date(val) : new Date(val.seconds * 1000));
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
}

function truncate(str, n = 20) {
  if (!str) return '-';
  return str.length > n ? str.slice(0, n) + '…' : str;
}

function relativeTime(isoString) {
  if (!isoString) return null;
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// Tracker state helpers 
const FIVE_MINUTES_MS = 5 * 60 * 1000;

function isDeviceStale(device) {
  const now        = Date.now();
  const lastMotion = device?.meta?.deviceLastMotionAt;
  const latestAt   = device?.latest?.at;
  const lastMotionMs = lastMotion ? now - lastMotion.toDate().getTime() : Infinity;
  const latestAtMs   = latestAt   ? now - latestAt.toDate().getTime()   : Infinity;
  return Math.min(lastMotionMs, latestAtMs) > FIVE_MINUTES_MS;
}

const TRACKER_ACCENT = {
  MOVING:  '#4ade80',
  IDLE:    '#93c5fd',
  NO_FIX:  '#fcd34d',
  OFFLINE: 'rgba(255,255,255,0.25)',
  UNKNOWN: 'rgba(255,255,255,0.15)',
};

const STATE_COLOR = {
  MOVING:  '#16a34a',
  IDLE:    '#3b82f6',
  NO_FIX:  '#d97706',
  OFFLINE: '#6b7280',
  UNKNOWN: '#9ca3af',
};

function deriveDeviceState(device) {
  const latest = device?.latest;
  if (!latest) return { state: 'UNKNOWN', label: 'Unknown', timestamp: null };
  const ts = latest.at?.toDate ? latest.at.toDate() : new Date(latest.at?.seconds * 1000);
  if (latest.payloadType === 'event') {
    if (latest.eventType === 'Start of movement') return { state: 'MOVING',  label: 'Moving',          timestamp: ts };
    if (latest.eventType === 'End of movement')   return { state: 'IDLE',    label: 'Stationary',      timestamp: ts };
  }
  if (latest.payloadType === 'location_fixed' || latest.payloadType === 'location_fixed_gps_limit')
    return latest.stillInMotion
      ? { state: 'MOVING',  label: 'Moving',          timestamp: ts }
      : { state: 'IDLE',    label: 'Stationary',      timestamp: ts };
  if (latest.payloadType === 'location_failure') return { state: 'NO_FIX',  label: 'Position failed', timestamp: ts };
  if (latest.payloadType === 'heartbeat')        return { state: 'IDLE',    label: 'Device OK',       timestamp: ts };
  if (latest.payloadType === 'shutdown')         return { state: 'OFFLINE', label: 'Shut down',       timestamp: ts };
  return { state: 'UNKNOWN', label: 'Unknown', timestamp: ts };
}

// Gateway helpers 
const GATEWAY_ACCENT = {
  ONLINE:     '#4ade80',
  OFFLINE:    '#fcd34d',
  NEVER_SEEN: 'rgba(255,255,255,0.2)',
};
const GATEWAY_STATE_COLOR = { ONLINE: '#16a34a', OFFLINE: '#d97706', NEVER_SEEN: '#9ca3af' };

function gatewayStateLabel(state) {
  if (state === 'ONLINE')     return 'Online';
  if (state === 'OFFLINE')    return 'Offline';
  if (state === 'NEVER_SEEN') return 'Never seen';
  return 'Unknown';
}

// Chirpstack device rename (gateway rename lives in GatewayPopup)

async function renameDevice(devEui, newName) {
  const getRes = await fetch(`/chirpstack/api/devices/${devEui}`);
  if (!getRes.ok) throw new Error(`GET failed: ${getRes.status}`);
  const { device } = await getRes.json();
  const putRes = await fetch(`/chirpstack/api/devices/${devEui}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ device: { ...device, name: newName } }),
  });
  if (!putRes.ok) throw new Error(`PUT failed: ${putRes.status}`);
}


async function renameGateway(gatewayId, newName) {
  // Always GET the full object first — the list response omits fields like
  // statsInterval which would be zeroed out if we spread a list-derived object.
  const getRes = await fetch(`/chirpstack/api/gateways/${gatewayId}`);
  if (!getRes.ok) throw new Error(`GET failed: ${getRes.status}`);
  const { gateway } = await getRes.json();
  const putRes = await fetch(`/chirpstack/api/gateways/${gatewayId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ gateway: { ...gateway, name: newName } }),
  });
  if (!putRes.ok) throw new Error(`PUT failed: ${putRes.status}`);
}

// Shared sub-components
function AccordionHeader({ label, count, open, onToggle }) {
  return (
    <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: open ? 8 : 0, cursor: 'pointer', userSelect: 'none' }}>
      <div style={{ fontSize: '0.63rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {count != null && (
          <span style={{ fontSize: '0.65rem', fontWeight: 800, background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 20, padding: '2px 8px' }}>{count}</span>
        )}
        <span style={{ color: 'rgba(255,255,255,0.5)', display: 'flex' }}>{open ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}</span>
      </div>
    </div>
  );
}

// Info row used inside the popup
function InfoRow({ label, value, mono, last }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: last ? 'none' : '1px solid #f1f5f9', gap: 12 }}>
      <span style={{ fontSize: '0.73rem', color: '#64748b', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '0.73rem', color: '#1e3a5f', fontWeight: 500, textAlign: 'right', wordBreak: 'break-all', fontFamily: mono ? 'monospace' : undefined }}>{value || '-'}</span>
    </div>
  );
}

// Section header inside popup
function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 4, marginTop: 2 }}>{children}</div>
  );
}

// Battery low SVG icon (outline battery with a small exclamation cap)
function BatteryLowIcon({ size = 12, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      {/* Battery body */}
      <rect x="1" y="6" width="18" height="12" rx="2" ry="2" />
      {/* Battery cap */}
      <line x1="23" y1="10" x2="23" y2="14" />
      {/* Low fill — just a thin red bar on the left inside */}
      <line x1="4" y1="10" x2="4" y2="14" strokeWidth="3" />
    </svg>
  );
}

// Device Popup
function DevicePopup({ device, nameOverride, onClose, onSeeTrail, onRenameSuccess }) {
  const [renamingName, setRenamingName] = useState(false);
  const [renameValue,  setRenameValue]  = useState('');
  const [saving,       setSaving]       = useState(false);
  const [renameError,  setRenameError]  = useState(null);
  const renameInputRef = useRef(null);

  const state      = deriveDeviceState(device);
  const stateColor = STATE_COLOR[state.state] ?? '#9ca3af';
  const name       = nameOverride ?? device.meta?.deviceName ?? device.meta?.deviceEui;
  const meta       = device.meta ?? {};
  const lvp        = device.lastValidPosition;
  const lastMotion = meta.deviceLastMotionAt;
  const batteryLow = meta.batteryLow === true;
  const batteryPct = meta.batteryPct ?? null;

  useEffect(() => { if (renamingName) { setRenameValue(name); renameInputRef.current?.focus(); renameInputRef.current?.select(); } }, [renamingName]);

  async function submitRename() {
    if (!renameValue.trim()) return;
    setSaving(true);
    setRenameError(null);
    try {
      await renameDevice(meta.deviceEui, renameValue.trim());
      onRenameSuccess(meta.deviceEui, renameValue.trim());
      setRenamingName(false);
    } catch (e) {
      setRenameError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '80vh', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND} 100%)`, padding: '16px 18px 14px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: stateColor, flexShrink: 0, boxShadow: `0 0 0 2px ${stateColor}44` }} />
              <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>{state.label}</span>
              {batteryLow && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.65rem', fontWeight: 700, background: 'rgba(239,68,68,0.3)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 20, padding: '2px 7px', whiteSpace: 'nowrap' }}>
                  <BatteryLowIcon size={10} color="#fca5a5" />
                  Low battery
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 7, width: 28, height: 28, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FiX size={14} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Status */}
        <div>
          <SectionLabel>Status</SectionLabel>
          <InfoRow label="Last update"         value={fmtDate(device.latest?.at)} />
          <InfoRow label="Last valid position" value={fmtDate(lvp?.at)} />
          <InfoRow label="Last movement"       value={fmtDate(lastMotion)} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', gap: 12 }}>
            <span style={{ fontSize: '0.73rem', color: '#64748b', flexShrink: 0 }}>Battery</span>
            {batteryPct !== null ? (
              <span style={{ fontSize: '0.72rem', fontWeight: 600,
                color: batteryPct < 20 ? '#dc2626' : batteryPct < 50 ? '#d97706' : '#16a34a' }}>
                {batteryPct}%{batteryPct < 20 ? ' — replace soon' : ''}
              </span>
            ) : batteryLow ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', fontWeight: 600, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '2px 8px' }}>
                <BatteryLowIcon size={11} color="#dc2626" />
                Low — replace soon
              </span>
            ) : (
              <span style={{ fontSize: '0.72rem', fontWeight: 500, color: '#16a34a' }}>OK</span>
            )}
          </div>
          {device.latest?.payloadType === 'location_failure' && device.latest?.positioningFailureReason && (
            <div style={{ marginTop: 6, fontSize: '0.72rem', color: '#dc2626', background: '#fef2f2', borderRadius: 6, padding: '5px 8px' }}>
              {device.latest.positioningFailureReason}
            </div>
          )}
        </div>

        <div style={{ borderTop: '1px solid #f1f5f9' }} />

        {/* Device info */}
        <div>
          <SectionLabel>Device Info</SectionLabel>

          {/* Name row - editable */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f1f5f9', gap: 8 }}>
            <span style={{ fontSize: '0.73rem', color: '#64748b', flexShrink: 0 }}>Name</span>
            {renamingName ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, flex: 1, justifyContent: 'flex-end' }}>
                <input
                  ref={renameInputRef}
                  value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setRenamingName(false); }}
                  disabled={saving}
                  style={{ flex: 1, minWidth: 0, fontSize: '0.73rem', fontWeight: 500, padding: '3px 7px', borderRadius: 5, border: `1px solid ${BRAND}`, outline: 'none', color: '#1e3a5f' }}
                />
                <button onClick={submitRename} disabled={saving} style={{ background: BRAND, border: 'none', borderRadius: 5, width: 24, height: 24, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiCheck size={12} />
                </button>
                <button onClick={() => { setRenamingName(false); setRenameError(null); }} disabled={saving} style={{ background: '#f1f5f9', border: 'none', borderRadius: 5, width: 24, height: 24, cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiX size={12} />
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span style={{ fontSize: '0.73rem', color: '#1e3a5f', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{name}</span>
                <button onClick={() => setRenamingName(true)} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f0f6ff', border: '1px solid #dbeafe', borderRadius: 20, padding: '3px 10px', cursor: 'pointer', color: BRAND, fontSize: '0.68rem', fontWeight: 600, flexShrink: 0 }}>
                  <FiEdit2 size={10} />
                  Edit
                </button>
              </div>
            )}
          </div>
          {renameError && <div style={{ fontSize: '0.68rem', color: '#dc2626', marginTop: 4 }}>{renameError}</div>}

          <InfoRow label="Device EUI"  value={meta.deviceEui}         mono />
          <InfoRow label="Profile ID"  value={truncate(meta.deviceProfileId ?? '-', 22)} mono />
          <InfoRow label="Application" value={meta.applicationName} />
          <InfoRow label="Created"     value={fmtDate(device.createdAt)} />
          <InfoRow label="Last seen"   value={fmtDate(device.lastSeenAt)} last />
        </div>

        {/* See Trail */}
        {onSeeTrail && (
          <button
            onClick={onSeeTrail}
            style={{
              width: '100%', padding: '9px 0', borderRadius: 8, border: 'none',
              background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND} 100%)`,
              color: '#fff', fontWeight: 600, fontSize: '0.83rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
              boxShadow: '0 2px 8px rgba(48,127,226,0.3)',
            }}
          >
            <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
            See Trail
          </button>
        )}
      </div>
    </div>
  );
}

// Gateway Popup
function GatewayPopup({ gw, nameOverride, onClose, onRenameSuccess }) {
  const [renamingName, setRenamingName] = useState(false);
  const [renameValue,  setRenameValue]  = useState('');
  const [saving,       setSaving]       = useState(false);
  const [renameError,  setRenameError]  = useState(null);
  const renameInputRef = useRef(null);

  const name       = nameOverride ?? gw.name ?? gw.gatewayId;
  const stateColor = GATEWAY_STATE_COLOR[gw.state] ?? '#9ca3af';
  const stateLabel = gatewayStateLabel(gw.state);

  useEffect(() => { if (renamingName) { setRenameValue(name); renameInputRef.current?.focus(); renameInputRef.current?.select(); } }, [renamingName]);

  async function submitRename() {
    if (!renameValue.trim()) return;
    setSaving(true);
    setRenameError(null);
    try {
      await renameGateway(gw.gatewayId, renameValue.trim());
      onRenameSuccess(gw.gatewayId, renameValue.trim());
      setRenamingName(false);
    } catch (e) {
      setRenameError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '80vh', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND} 100%)`, padding: '16px 18px 14px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: stateColor, flexShrink: 0, boxShadow: `0 0 0 2px ${stateColor}44` }} />
              <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>{stateLabel}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 7, width: 28, height: 28, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FiX size={14} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Status */}
        <div>
          <SectionLabel>Status</SectionLabel>
          <InfoRow label="State"         value={stateLabel} />
          <InfoRow label="Last seen"     value={gw.lastSeenAt ? `${fmtDate(gw.lastSeenAt)} (${relativeTime(gw.lastSeenAt)})` : '-'} />
          <InfoRow label="Stats interval" value={gw.statsInterval ? `${gw.statsInterval}s` : '-'} last />
        </div>

        <div style={{ borderTop: '1px solid #f1f5f9' }} />

        {/* Gateway info */}
        <div>
          <SectionLabel>Gateway Info</SectionLabel>

          {/* Name row - editable */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f1f5f9', gap: 8 }}>
            <span style={{ fontSize: '0.73rem', color: '#64748b', flexShrink: 0 }}>Name</span>
            {renamingName ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, flex: 1, justifyContent: 'flex-end' }}>
                <input
                  ref={renameInputRef}
                  value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setRenamingName(false); }}
                  disabled={saving}
                  style={{ flex: 1, minWidth: 0, fontSize: '0.73rem', fontWeight: 500, padding: '3px 7px', borderRadius: 5, border: `1px solid ${BRAND}`, outline: 'none', color: '#1e3a5f' }}
                />
                <button onClick={submitRename} disabled={saving} style={{ background: BRAND, border: 'none', borderRadius: 5, width: 24, height: 24, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiCheck size={12} />
                </button>
                <button onClick={() => { setRenamingName(false); setRenameError(null); }} disabled={saving} style={{ background: '#f1f5f9', border: 'none', borderRadius: 5, width: 24, height: 24, cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiX size={12} />
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span style={{ fontSize: '0.73rem', color: '#1e3a5f', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{name}</span>
                <button onClick={() => setRenamingName(true)} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f0f6ff', border: '1px solid #dbeafe', borderRadius: 20, padding: '3px 10px', cursor: 'pointer', color: BRAND, fontSize: '0.68rem', fontWeight: 600, flexShrink: 0 }}>
                  <FiEdit2 size={10} />
                  Edit
                </button>
              </div>
            )}
          </div>
          {renameError && <div style={{ fontSize: '0.68rem', color: '#dc2626', marginTop: 4 }}>{renameError}</div>}

          <InfoRow label="Gateway ID"  value={gw.gatewayId}    mono />
          <InfoRow label="Region"      value={gw.metadata?.region_common_name} />
          <InfoRow label="Description" value={gw.description || '-'} />
          <InfoRow label="Created"     value={fmtDate(gw.createdAt)} />
          <InfoRow label="Updated"     value={fmtDate(gw.updatedAt)} last />
        </div>
      </div>
    </div>
  );
}

// Main component
export default function Sidebar({
  title = "Asset Tracking",
  applications, activeApp, setActiveApp,
  devices,
  assetStateFilter, setAssetStateFilter,
  onSeeTrail,
  tenant,
  hiddenEuis = new Set(),
  onToggleHidden,
  // Gateway state — managed by index.js and shared with the map
  gateways = [], gwLoading = false, gwError = null, pollGateways,
}) {
  const [collapsed,           setCollapsed]           = useState(false);
  const [trackersOpen,        setTrackersOpen]        = useState(true);
  const [gatewaysOpen,        setGatewaysOpen]        = useState(true);
  const [selectedItem,        setSelectedItem]        = useState(null);
  const [deviceNameOverrides, setDeviceNameOverrides] = useState({});
  const [gwNameOverrides,     setGwNameOverrides]     = useState({});

  const sidebarRef = useRef(null);
  const locations     = useSelector(selectAirportLocations) ?? [];
  const wifiLocations = useSelector(selectWifiLocations) ?? {};
  const centroids     = useMemo(() => buildCentroids(locations), [locations]);

  // Open popup — flip above click point when insufficient space below
  function openItem(e, type, data) {
    e.preventDefault();
    const rect     = sidebarRef.current?.getBoundingClientRect();
    const popupX   = rect ? rect.right + 12 : 320;
    const POPUP_H  = 560;
    const spaceBelow = window.innerHeight - e.clientY - 16;
    const popupY   = spaceBelow >= POPUP_H
      ? e.clientY
      : Math.max(16, e.clientY - POPUP_H);
    setSelectedItem({ type, data, popupX, popupY });
  }

  const sidebarBg = `linear-gradient(175deg, ${BRAND_DARK} 0%, ${BRAND} 100%)`;

  // Collapsed rail
  if (collapsed) {
    return (
      <div style={{ width: 50, height: '100%', flexShrink: 0, borderRadius: 12, background: sidebarBg, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 14, boxShadow: '0 4px 20px rgba(48,127,226,0.3)' }}>
        <button onClick={() => setCollapsed(false)} title="Expand" style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.15)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
          <FiChevronRight size={14} />
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Popup panel */}
      {selectedItem && (
        <>
          {/* Backdrop */}
          <div onClick={() => setSelectedItem(null)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />

          {/* Panel */}
          <div style={{
            position: 'fixed',
            left: selectedItem.popupX,
            top: selectedItem.popupY,
            width: 340,
            maxHeight: `calc(100vh - ${selectedItem.popupY}px - 16px)`,
            zIndex: 9999,
            background: '#fff',
            borderRadius: 14,
            border: '1px solid #dbeafe',
            boxShadow: '0 12px 48px rgba(48,127,226,0.2)',
            overflow: 'hidden',
          }}>
            {selectedItem.type === 'device' ? (
              <DevicePopup
                device={selectedItem.data}
                nameOverride={deviceNameOverrides[selectedItem.data.meta?.deviceEui]}
                onClose={() => setSelectedItem(null)}
                onSeeTrail={onSeeTrail ? () => { onSeeTrail(selectedItem.data); setSelectedItem(null); } : null}
                onRenameSuccess={(eui, name) => setDeviceNameOverrides(p => ({ ...p, [eui]: name }))}
              />
            ) : (
              <GatewayPopup
                gw={selectedItem.data}
                nameOverride={gwNameOverrides[selectedItem.data.gatewayId]}
                onClose={() => setSelectedItem(null)}
                onRenameSuccess={(id, name) => {
                  setGwNameOverrides(p => ({ ...p, [id]: name }));
                  setGateways(prev => prev.map(g => g.gatewayId === id ? { ...g, name } : g));
                }}
              />
            )}
          </div>
        </>
      )}

      {/*  Sidebar */}
      <div ref={sidebarRef} style={{ width: 300, height: '100%', flexShrink: 0, borderRadius: 12, background: sidebarBg, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 20px rgba(48,127,226,0.3)' }}>

        {/* Header */}
        <div style={{ padding: '16px 16px 14px', flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="w-100" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly' }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
              </div>
              <span className="w-100" style={{ fontSize: '0.88rem', fontWeight: 700, color: '#fff', letterSpacing: '-0.01em', textAlign: 'center' }}>{title}</span>
            </div>
            <button onClick={() => setCollapsed(true)} title="Collapse" style={{ width: 28, height: 28, borderRadius: 7, flexShrink: 0, border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.12)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <FiChevronLeft size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'hidden', padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Filter by Assets */}
          <div style={{ flexShrink: 0 }}>
            <div style={{ fontSize: '0.63rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>Filter by Assets</div>
            {!applications || Object.keys(applications).length === 0 ? (
              <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center', padding: '8px 0' }}>No assets found</div>
            ) : (
              <Dropdown className="w-100">
                <Dropdown.Toggle className="d-flex align-items-center justify-content-between w-100 px-3 py-2" variant="outline-light" style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 8, fontSize: '0.82rem', color: '#fff', fontWeight: 500, boxShadow: 'none' }}>
                  {activeApp || 'Select application'}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100 p-1" style={{ background: '#fff', border: '1px solid #dbeafe', borderRadius: 8, boxShadow: '0 8px 24px rgba(48,127,226,0.18)' }}>
                  {Object.keys(applications).map(appName => (
                    <Dropdown.Item key={appName} active={appName === activeApp} onClick={() => setActiveApp(appName)} style={{ borderRadius: 6, fontSize: '0.82rem', color: '#1e3a5f' }}>{appName}</Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>

          {/* Filter by Asset State */}
          <div style={{ flexShrink: 0 }}>
            <div style={{ fontSize: '0.63rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>Filter by Asset State</div>
            <div style={{ display: 'flex', gap: 4, background: 'rgba(0,0,0,0.18)', borderRadius: 9, padding: 4 }}>
              {['Idle', 'Active', 'All'].map(type => {
                const active = (assetStateFilter || 'All') === type;
                return (
                  <button key={type} onClick={() => setAssetStateFilter(type === 'All' ? null : type)} style={{ flex: 1, padding: '5px 0', borderRadius: 6, border: 'none', fontSize: '0.78rem', fontWeight: active ? 700 : 500, cursor: 'pointer', background: active ? '#fff' : 'transparent', color: active ? BRAND : 'rgba(255,255,255,0.65)', boxShadow: active ? '0 1px 4px rgba(0,0,0,0.15)' : 'none', transition: 'all 0.15s ease' }}>
                    {type}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── All Trackers accordion ── */}
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flexShrink: trackersOpen ? 1 : 0 }}>
            <AccordionHeader label="All Trackers" count={devices?.length ?? 0} open={trackersOpen} onToggle={() => setTrackersOpen(o => !o)} />
            {trackersOpen && (
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {!devices?.length ? (
                  <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '16px 0' }}>No trackers yet</div>
                ) : devices.map(d => {
                  const devEui   = d.meta.deviceEui;
                  const name     = deviceNameOverrides[devEui] ?? d.meta.deviceName;
                  const state    = deriveDeviceState(d);
                  const accent   = TRACKER_ACCENT[state.state] || TRACKER_ACCENT.UNKNOWN;
                  const stale    = isDeviceStale(d);
                  const location = resolveLocationLabel(d, locations, centroids, wifiLocations)?.label;
                  const active   = selectedItem?.type === 'device' && selectedItem?.data?.meta?.deviceEui === devEui;
                  const batteryLow = d.meta?.batteryLow === true;
                  const batteryPct = d.meta?.batteryPct ?? null;
                  const hidden   = hiddenEuis.has(d.id);
                  return (
                    <div
                      key={devEui}
                      onClick={e => openItem(e, 'device', d)}
                      onContextMenu={e => openItem(e, 'device', d)}
                      style={{ display: 'flex', alignItems: 'stretch', background: active ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.1)', border: `1px solid ${active ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.15)'}`, borderRadius: 8, marginBottom: 5, cursor: 'pointer', overflow: 'hidden', transition: 'background 0.12s', opacity: hidden ? 0.45 : 1 }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                    >
                      <div style={{ width: 3, background: accent, flexShrink: 0 }} />
                      <div style={{ padding: '8px 10px', flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                          {batteryPct !== null ? (
                            <span
                              title={`Battery ${batteryPct}%`}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.6rem', fontWeight: 700, borderRadius: 20, padding: '1px 5px', whiteSpace: 'nowrap', flexShrink: 0,
                                background: batteryPct < 20 ? 'rgba(239,68,68,0.22)' : batteryPct < 50 ? 'rgba(251,191,36,0.2)' : 'rgba(34,197,94,0.15)',
                                color:      batteryPct < 20 ? '#fca5a5'              : batteryPct < 50 ? '#fcd34d'              : '#86efac',
                                border:     `1px solid ${batteryPct < 20 ? 'rgba(239,68,68,0.35)' : batteryPct < 50 ? 'rgba(251,191,36,0.3)' : 'rgba(34,197,94,0.25)'}`,
                              }}
                            >
                              <BatteryLowIcon size={9} />
                              {batteryPct}%
                            </span>
                          ) : batteryLow && (
                            <span title="Low battery" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.6rem', fontWeight: 700, background: 'rgba(239,68,68,0.22)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 20, padding: '1px 5px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                              <BatteryLowIcon size={9} />
                              Low
                            </span>
                          )}
                          {stale && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.6rem', fontWeight: 700, background: 'rgba(252,211,77,0.2)', color: '#f7d346', border: '1px solid rgba(252,211,77,0.3)', borderRadius: 20, padding: '1px 5px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                              5min+
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.79)' }}>{state.label}</div>
                        {location && <div style={{ fontSize: '0.67rem', color: 'rgba(255,255,255,0.5)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{location}</div>}
                      </div>
                      <button
                        title={hidden ? 'Unhide device' : 'Hide device'}
                        onClick={e => { e.stopPropagation(); onToggleHidden?.(d.id); }}
                        style={{ alignSelf: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 8px', color: hidden ? '#fcd34d' : 'rgba(255,255,255,0.35)', flexShrink: 0, display: 'flex' }}
                      >
                        {hidden ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', flexShrink: 0 }} />

          {/* ── All Gateways accordion ── */}
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flexShrink: gatewaysOpen ? 1 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: gatewaysOpen ? 8 : 0 }}>
              <AccordionHeader label="All Gateways" count={gateways.length || null} open={gatewaysOpen} onToggle={() => setGatewaysOpen(o => !o)} />
              <button onClick={pollGateways} title="Refresh" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.45)', padding: 2, display: 'flex', lineHeight: 1 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              </button>
            </div>
            {gatewaysOpen && (
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {gwLoading && !gateways.length && <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '16px 0' }}>Loading…</div>}
                {gwError && <div style={{ fontSize: '0.72rem', color: '#fcd34d', padding: '8px 10px', background: 'rgba(0,0,0,0.15)', borderRadius: 6 }}>{gwError}</div>}
                {!gwLoading && !gwError && gateways.length === 0 && <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '16px 0' }}>No gateways found</div>}
                {gateways.map(gw => {
                  const accent = GATEWAY_ACCENT[gw.state] ?? GATEWAY_ACCENT.NEVER_SEEN;
                  const label  = gatewayStateLabel(gw.state);
                  const seen   = relativeTime(gw.lastSeenAt);
                  const name   = gwNameOverrides[gw.gatewayId] ?? gw.name ?? gw.gatewayId;
                  const active = selectedItem?.type === 'gateway' && selectedItem?.data?.gatewayId === gw.gatewayId;
                  return (
                    <div
                      key={gw.gatewayId}
                      onClick={e => openItem(e, 'gateway', gw)}
                      onContextMenu={e => openItem(e, 'gateway', gw)}
                      style={{ display: 'flex', alignItems: 'stretch', background: active ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.07)', border: `1px solid ${active ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.12)'}`, borderRadius: 8, marginBottom: 5, overflow: 'hidden', cursor: 'pointer', transition: 'background 0.12s' }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                    >
                      <div style={{ width: 3, background: accent, flexShrink: 0 }} />
                      <div style={{ padding: '7px 10px', flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>{name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: '0.7rem', color: accent === GATEWAY_ACCENT.NEVER_SEEN ? 'rgba(255,255,255,0.4)' : accent }}>{label}</span>
                          {seen && <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)' }}>· {seen}</span>}
                        </div>
                        <div style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.3)', marginTop: 1, fontFamily: 'monospace' }}>{gw.gatewayId}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
}
